//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import QueryParamsModel from './queryParams.model';

/**
 * @service QueryParamsService
 * @description Service for managing Quality Parameters (QP) definitions within the aquaculture system.
 * Quality Parameters represent the specific metrics or attributes that are measured to assess water quality,
 * soil condition, or other environmental factors in aquaculture ponds.
 *
 * Examples include:
 * - pH
 * - Dissolved Oxygen (DO)
 * - Temperature
 * - Salinity
 * - Ammonia
 *
 * This service provides the interface to retrieve the master list of these parameters from the backend.
 * It is essential for configuring what data can be collected in logs, sensors, and manual readings.
 *
 * @feature 1. Retrieve all defined Quality Parameters.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all parameters are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<QueryParamsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/queryparams/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class QueryParamsService extends Service<QueryParamsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/queryparams/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/queryparams/');
  }

  /**
   * @method getAllQueryParam
   * @async
   * @description Fetch all Quality Parameter records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all parameter definitions are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<QueryParamsModel>>} Promise resolving to an array of QueryParamsModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * queryParamsService.getAllQueryParam().then(params => {
   *   console.log('Quality Parameters:', params);
   * });
   */
  public getAllQueryParam() {
    return new Promise<Array<QueryParamsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<QueryParamsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
